import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Chip,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Divider,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import EditIcon from '@mui/icons-material/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { makeGetRequest, makePostRequest } from '../../utils/api'
import urls from '../../utils/urls'

const CreateFromRecipesDialog = ({ open, onClose, onCreate, showSnackbar }) => {
  const [activeStep, setActiveStep] = useState(0)
  const [listName, setListName] = useState('')
  const [recipes, setRecipes] = useState([])
  const [selectedRecipes, setSelectedRecipes] = useState([])
  const [ingredients, setIngredients] = useState([])
  const [selectedIngredients, setSelectedIngredients] = useState({})
  const [editingIngredient, setEditingIngredient] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  const steps = ['Sélectionner les Recettes', 'Vérifier les Ingrédients', 'Créer la Liste']

  useEffect(() => {
    if (open) {
      fetchRecipes()
    }
  }, [open])

  useEffect(() => {
    if (activeStep === 1 && selectedRecipes.length > 0) {
      extractIngredients()
    }
  }, [activeStep, selectedRecipes])

  const fetchRecipes = async () => {
    setLoading(true)
    try {
      // Using the Personnal module's recipe endpoint with pagination
      const response = await makeGetRequest(urls.crudRecettesOperations + '?limit=10000', {}, true)
      if (response?.status === 200) {
        const recipeData = response.data.results || response.data || []
        setRecipes(recipeData)
        console.log('Loaded recipes:', recipeData.length)
      }
    } catch (error) {
      console.error('Error fetching recipes:', error)
      showSnackbar('Erreur lors du chargement des recettes. Assurez-vous que le module Recettes est disponible.', 'warning')
      // Provide fallback empty array
      setRecipes([])
    } finally {
      setLoading(false)
    }
  }

  const extractIngredients = async () => {
    // Extract ingredients from selected recipes
    const allIngredients = []
    const selected = {}

    // Fetch ingredients for each selected recipe
    for (const recipeId of selectedRecipes) {
      const recipe = recipes.find(r => r.id === recipeId)
      if (recipe) {
        try {
          // Fetch BillOfIngredient for this recipe
          const response = await makeGetRequest(
            `/api/Personnal/billofingredients-operations/?RecipeId=${recipeId}`,
            {},
            true
          )
          
          if (response?.status === 200) {
            const ingredients = response.data.results || response.data || []
            ingredients.forEach(ing => {
              const ingredientKey = `${recipeId}-${ing.id}`
              allIngredients.push({
                id: ing.id,
                name: ing.NomIngredient,
                ingredient_name: ing.NomIngredient,
                quantity: ing.Quantite,
                amount: ing.Quantite,
                unit: ing.um?.Designation || ing.um?.UM || 'pcs',
                measurement_unit: ing.um?.Designation || ing.um?.UM || 'pcs',
                aisle: ing.Rayon,
                recipeId,
                recipeName: recipe.Nom,
                key: ingredientKey,
              })
              // Select all by default
              selected[ingredientKey] = true
            })
          }
        } catch (error) {
          console.error(`Error fetching ingredients for recipe ${recipeId}:`, error)
        }
      }
    }

    setIngredients(allIngredients)
    setSelectedIngredients(selected)
  }

  const handleRecipeToggle = (recipeId) => {
    setSelectedRecipes(prev => {
      if (prev.includes(recipeId)) {
        return prev.filter(id => id !== recipeId)
      } else {
        return [...prev, recipeId]
      }
    })
  }

  const handleIngredientToggle = (key) => {
    setSelectedIngredients(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleSelectAllIngredients = (selectAll) => {
    const updated = {}
    ingredients.forEach(ing => {
      updated[ing.key] = selectAll
    })
    setSelectedIngredients(updated)
  }

  const handleEditIngredient = (ingredient) => {
    setEditingIngredient({
      ...ingredient,
      originalKey: ingredient.key,
    })
  }

  const handleSaveEditedIngredient = () => {
    if (editingIngredient) {
      setIngredients(prev => prev.map(ing => 
        ing.key === editingIngredient.originalKey ? { ...editingIngredient } : ing
      ))
      setEditingIngredient(null)
    }
  }

  const handleNext = () => {
    if (activeStep === 0 && selectedRecipes.length === 0) {
      showSnackbar('Veuillez sélectionner au moins une recette', 'warning')
      return
    }
    setActiveStep(prev => prev + 1)
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
  }

  const handleCreate = async () => {
    setCreating(true)
    
    // Prepare selected ingredients data
    const selectedIngredientsData = ingredients
      .filter(ing => selectedIngredients[ing.key])
      .map(ing => ({
        name: ing.name || ing.ingredient_name,
        quantity: ing.quantity || ing.amount || 1,
        unit: ing.unit || ing.measurement_unit || 'pcs',
        notes: `From recipe: ${ing.recipeName}`,
      }))

    const data = {
      recipe_ids: selectedRecipes,
      selected_ingredients: selectedIngredientsData,
      list_name: listName.trim() || `Liste de course du ${new Date().toLocaleDateString()}`,
    }

    try {
      const response = await makePostRequest(
        `${urls.shoppingLists}create_from_recipes/`,
        data,
        {},
        true
      )
      
      if (response?.status === 201) {
        showSnackbar('Liste de courses créée avec les ingrédients de la recette !', 'success')
        onCreate(response.data)
        handleClose()
      }
    } catch (error) {
      console.error('Error creating list from recipes:', error)
      showSnackbar('Erreur lors de la création de la liste depuis les recettes', 'error')
    } finally {
      setCreating(false)
    }
  }

  const handleClose = () => {
    setActiveStep(0)
    setListName('')
    setSelectedRecipes([])
    setIngredients([])
    setSelectedIngredients({})
    setEditingIngredient(null)
    setSearchQuery('')
    onClose()
  }

  const filteredRecipes = recipes.filter(recipe => {
    if (!searchQuery.trim()) return true
    
    const searchLower = searchQuery.toLowerCase()
    const name = (recipe.Nom || recipe.name || recipe.title || '').toLowerCase()
    const category = (recipe.categorie?.Name || recipe.category?.name || '').toLowerCase()
    const mealType = (recipe.TypeDeRepas || recipe.meal_type || '').toLowerCase()
    
    return name.includes(searchLower) || 
           category.includes(searchLower) || 
           mealType.includes(searchLower)
  })

  const groupedIngredients = ingredients.reduce((acc, ing) => {
    const recipe = ing.recipeName
    if (!acc[recipe]) {
      acc[recipe] = []
    }
    acc[recipe].push(ing)
    return acc
  }, {})

  const selectedCount = Object.values(selectedIngredients).filter(Boolean).length

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestaurantIcon color="primary" />
          <Typography variant="h6">Créer une Liste depuis les Recettes</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Select Recipes */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sélectionnez les recettes pour extraire les ingrédients
            </Typography>

            <TextField
              fullWidth
              placeholder="Rechercher des recettes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredRecipes.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Aucune recette trouvée. Ajoutez d'abord des recettes dans le module Recettes.
                </Typography>
              </Paper>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredRecipes.map((recipe) => (
                  <ListItem
                    key={recipe.id}
                    button
                    onClick={() => handleRecipeToggle(recipe.id)}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <Checkbox
                      checked={selectedRecipes.includes(recipe.id)}
                      edge="start"
                    />
                    <ListItemText
                      primary={recipe.Nom}
                      secondary={
                        <Box>
                          {recipe.categorie?.Name && (
                            <Chip label={recipe.categorie.Name} size="small" sx={{ mr: 1 }} />
                          )}
                          {recipe.TypeDeRepas && (
                            <Chip label={recipe.TypeDeRepas} size="small" variant="outlined" sx={{ mr: 1 }} />
                          )}
                          {recipe.Servir > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {recipe.Servir} portions
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {selectedRecipes.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {selectedRecipes.length} recette(s) sélectionnée(s)
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: Review Ingredients */}
        {activeStep === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Vérifiez et modifiez les ingrédients ({selectedCount} sélectionné(s))
              </Typography>
              <Box>
                <Button
                  size="small"
                  onClick={() => handleSelectAllIngredients(true)}
                  sx={{ mr: 1 }}
                >
                  Tout Sélectionner
                </Button>
                <Button
                  size="small"
                  onClick={() => handleSelectAllIngredients(false)}
                >
                  Tout Désélectionner
                </Button>
              </Box>
            </Box>

            <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
              {Object.entries(groupedIngredients).map(([recipeName, recipeIngredients]) => (
                <Accordion key={recipeName} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RestaurantIcon fontSize="small" />
                      <Typography variant="subtitle1">{recipeName}</Typography>
                      <Chip 
                        label={`${recipeIngredients.filter(ing => selectedIngredients[ing.key]).length}/${recipeIngredients.length}`}
                        size="small"
                        color="primary"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      {recipeIngredients.map((ingredient) => (
                        <ListItem key={ingredient.key}>
                          <Checkbox
                            checked={selectedIngredients[ingredient.key] || false}
                            onChange={() => handleIngredientToggle(ingredient.key)}
                          />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">
                                  {ingredient.name || ingredient.ingredient_name}
                                </Typography>
                                {selectedIngredients[ingredient.key] && (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                )}
                              </Box>
                            }
                            secondary={`${ingredient.quantity || ingredient.amount || 1} ${ingredient.unit || ingredient.measurement_unit || 'pcs'}`}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleEditIngredient(ingredient)}
                            disabled={!selectedIngredients[ingredient.key]}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {selectedCount === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Aucun ingrédient sélectionné. Sélectionnez au moins un ingrédient pour continuer.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 3: Finalize */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Vérifiez votre sélection et créez la liste
            </Typography>

            <TextField
              fullWidth
              label="Nom de la Liste (Optionnel)"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder={`Liste de course du ${new Date().toLocaleDateString()}`}
              sx={{ mb: 3 }}
            />

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Résumé
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Recettes :
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {selectedRecipes.length}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Ingrédients :
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {selectedCount}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Le système va automatiquement :
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                <li>Fusionner les ingrédients en double</li>
                <li>Convertir les unités en mesures standard</li>
                <li>Ajouter vos articles de bibliothèque par défaut</li>
                <li>Ajouter les articles récurrents qui sont dus</li>
              </Box>
            </Alert>

            <Typography variant="body2" color="text.secondary">
              Recettes sélectionnées :
            </Typography>
            <Box sx={{ mt: 1 }}>
              {selectedRecipes.map(recipeId => {
                const recipe = recipes.find(r => r.id === recipeId)
                return (
                  <Chip
                    key={recipeId}
                    label={recipe?.Nom}
                    sx={{ mr: 1, mb: 1 }}
                    icon={<RestaurantIcon />}
                  />
                )
              })}
            </Box>
          </Box>
        )}

        {/* Edit Ingredient Dialog */}
        <Dialog
          open={editingIngredient !== null}
          onClose={() => setEditingIngredient(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Modifier l'Ingrédient</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom"
                  value={editingIngredient?.name || editingIngredient?.ingredient_name || ''}
                  onChange={(e) => setEditingIngredient(prev => ({
                    ...prev,
                    name: e.target.value,
                    ingredient_name: e.target.value
                  }))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Quantité"
                  type="number"
                  value={editingIngredient?.quantity || editingIngredient?.amount || 1}
                  onChange={(e) => setEditingIngredient(prev => ({
                    ...prev,
                    quantity: parseFloat(e.target.value),
                    amount: parseFloat(e.target.value)
                  }))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Unité"
                  value={editingIngredient?.unit || editingIngredient?.measurement_unit || ''}
                  onChange={(e) => setEditingIngredient(prev => ({
                    ...prev,
                    unit: e.target.value,
                    measurement_unit: e.target.value
                  }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingIngredient(null)}>Annuler</Button>
            <Button onClick={handleSaveEditedIngredient} variant="contained">
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          Annuler
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={creating}>
            Retour
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext} 
            variant="contained"
            disabled={activeStep === 0 && selectedRecipes.length === 0}
          >
            Suivant
          </Button>
        ) : (
          <Button 
            onClick={handleCreate} 
            variant="contained"
            disabled={creating || selectedCount === 0}
            startIcon={creating ? <CircularProgress size={20} /> : null}
          >
            {creating ? 'Création en cours...' : 'Créer la Liste'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CreateFromRecipesDialog
